import { toast } from 'react-hot-toast'
import { MessageStatusEvent } from '../../../shared-src/message-send'

/*
	In-memory registry of messages sent via `messages.sendUserMessage()` whose outcome has not arrived yet,
	keyed by the clientMessageId the sender generated.

	Every send ends in exactly one outcome event: `messages created` (stored) or `messages status`
	(failed). The registry lets the composer that made the send react to a failure (toast + restore
	the text), and is dropped when the stored message shows up. Nothing here times out: a message
	stays pending until the server says what happened.

	Outcome events are plain socket emits: if the sender's socket is disconnected when the outcome
	fires, it is not replayed, and nothing times out (slice C / later work).

	The registry does not survive a page reload. A failure event for an unknown clientMessageId is
	still shown to the user as a plain toast, but there is no composer state left to restore.
*/

interface PendingSend {
	ulid?: string
	onFailed: (reason: string) => void
}

const pendingByClientMessageId = new Map<string, PendingSend>()

//	The stored event can beat sendUserMessage()'s acknowledgement, so the ulid is not yet known on the entry
//	when the message arrives. Ulids settled that way are remembered briefly so the late
//	setPendingSendUlid() can drop the entry instead of leaking it. Bounded, oldest evicted first.
const MAX_SETTLED_ULIDS = 50
const settledUlids = new Set<string>()

export function registerPendingSend(clientMessageId: string, onFailed: (reason: string) => void): void {
	pendingByClientMessageId.set(clientMessageId, { onFailed })
}

export function removePendingSend(clientMessageId: string): void {
	pendingByClientMessageId.delete(clientMessageId)
}

export function setPendingSendUlid(clientMessageId: string, ulid: string): void {
	const entry = pendingByClientMessageId.get(clientMessageId)
	if (!entry) return
	if (settledUlids.delete(ulid)) {
		pendingByClientMessageId.delete(clientMessageId)
		return
	}
	entry.ulid = ulid
}

export function settlePendingSendByUlid(ulid: string): void {
	let hasEntryWithoutUlid = false
	for (const [clientMessageId, entry] of pendingByClientMessageId) {
		if (entry.ulid === ulid) {
			pendingByClientMessageId.delete(clientMessageId)
			return
		}
		if (entry.ulid === undefined)
			hasEntryWithoutUlid = true
	}
	//	Only a send still awaiting its ulid can be beaten by its stored event; remembering every other
	//	ulid (other users' messages) would just churn the bounded set.
	if (!hasEntryWithoutUlid) return
	settledUlids.add(ulid)
	if (settledUlids.size > MAX_SETTLED_ULIDS) {
		const oldest = settledUlids.values().next().value
		if (oldest !== undefined)
			settledUlids.delete(oldest)
	}
}

export function handleSendStatus(event: MessageStatusEvent): void {
	if (event.status !== 'failed') return
	const entry = pendingByClientMessageId.get(event.clientMessageId)
	if (!entry) {
		toast.error(event.reason)
		console.warn(event.reason)
		return
	}
	pendingByClientMessageId.delete(event.clientMessageId)
	entry.onFailed(event.reason)
}
